import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, ConfirmDialog } from '@alchemiesolns/ui';
import { createFocusTrap } from '@alchemiesolns/ui';

const SKETCHER_URL = 'https://alchemie-chemistry-sketcher.web.app/';

interface SketcherOverlayProps {
  open: boolean;
  initialConfig: string;
  label: string;
  onSave: (config: string) => void;
  onClose: () => void;
}

export function SketcherOverlay({
  open,
  initialConfig,
  label,
  onSave,
  onClose,
}: SketcherOverlayProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [latestConfig, setLatestConfig] = useState(initialConfig);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Reset state when overlay opens
  useEffect(() => {
    if (open) {
      setLatestConfig(initialConfig);
      setShowConfirmDialog(false);
      setIframeLoaded(false);
    }
  }, [open, initialConfig]);

  // Setup focus trap when modal opens
  useEffect(() => {
    if (open && modalRef.current) {
      const cleanup = createFocusTrap(modalRef.current);
      return cleanup;
    }
  }, [open]);

  // Listen for messages from the sketcher iframe
  useEffect(() => {
    if (!open) return;

    const handleMessage = (event: MessageEvent) => {
      // Validate origin for security
      if (!event.origin.includes('alchemie-chemistry-sketcher.web.app')) return;

      if (event.data?.type === 'configuration') {
        setLatestConfig(event.data.configuration);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [open]);

  // Send initial config to sketcher when iframe loads
  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
    if (iframeRef.current?.contentWindow && initialConfig) {
      // Small delay to ensure sketcher is ready
      setTimeout(() => {
        iframeRef.current?.contentWindow?.postMessage(
          { type: 'set-configuration', configuration: initialConfig },
          '*'
        );
      }, 500);
    }
  }, [initialConfig]);

  const hasChanges = latestConfig !== initialConfig;

  const handleCloseClick = () => {
    if (hasChanges) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  const handleSaveClick = () => {
    onSave(latestConfig);
    onClose();
  };

  const handleDiscardConfirm = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  const handleSaveConfirm = () => {
    onSave(latestConfig);
    setShowConfirmDialog(false);
    onClose();
  };

  const handleContinueEditing = () => {
    setShowConfirmDialog(false);
  };

  if (!open) return null;

  return (
    <div className="sketcher-overlay">
      <div ref={modalRef} className="sketcher-modal" role="dialog" aria-modal="true" aria-labelledby="sketcher-title">
        <div className="sketcher-header">
          <h2 id="sketcher-title" className="sketcher-title">Edit {label}</h2>
          <button
            onClick={handleCloseClick}
            className="sketcher-close-btn"
            aria-label="Close"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="sketcher-iframe-container">
          {!iframeLoaded && (
            <div className="sketcher-loading">Loading Sketcher...</div>
          )}
          <iframe
            ref={iframeRef}
            src={SKETCHER_URL}
            className="sketcher-iframe"
            onLoad={handleIframeLoad}
            title={`Molecular structure editor for ${label}`}
          />
        </div>
        <div className="sketcher-footer">
          <Button variant="primary" onClick={handleSaveClick}>
            Save
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={showConfirmDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. What would you like to do?"
        buttons={[
          { label: 'Discard', onClick: handleDiscardConfirm },
          { label: 'Save', onClick: handleSaveConfirm, variant: 'primary' },
          { label: 'Continue Editing', onClick: handleContinueEditing, variant: 'secondary' },
        ]}
      />
    </div>
  );
}
